import { AuthController } from "@somewear/auth";
import Config from "@web/config/Config";
import { firstValueFrom } from "rxjs";

export interface DocsFeature {
	key: string;
	name: string;
	enabled: boolean;
}

export interface DocsAuthorizationContext {
	organizationId: string | null;
	organizationRole: string | null;
	workspaceId: string | null;
	workspaceRole: string | null;
	features: DocsFeature[];
}

export interface DocsSession {
	docsOrigin: string;
	accessToken: string;
	expiresAt: string;
	expiresInSeconds: number;
	authorizationContext: DocsAuthorizationContext;
}

export class DocsSessionRequestError extends Error {
	constructor(public readonly status: number) {
		super(`Docs session request failed with ${status}`);
		this.name = "DocsSessionRequestError";
	}
}

export function docsSessionErrorMessage(error: unknown): string {
	return error instanceof DocsSessionRequestError && error.status === 403
		? "You don't have permission to view Docs."
		: "Docs could not be opened.";
}

export async function requestDocsSession(context: {
	requestedDocsOrigin: string;
	accountId?: string;
	organizationId?: string;
}): Promise<DocsSession> {
	const tokenString = await firstValueFrom(AuthController.tokenString$());
	if (!tokenString) throw new Error("A signed-in Somewear session is required");

	const headers = new Headers({
		Authorization: tokenString,
		"Content-Type": "application/json",
		"x-api-key": Config.somewear.apiKey,
		"x-platform": "Web",
	});
	if (context.accountId != null) headers.set("x-user-account-id", context.accountId);
	if (context.organizationId != null) headers.set("x-organization-id", context.organizationId);

	const baseUrl =
		Config.somewear.baseUrl === "auto"
			? `${window.location.origin}/`
			: new URL(Config.somewear.baseUrl, window.location.origin).href;
	const response = await fetch(new URL(Config.somewear.api.docsEnter, baseUrl), {
		method: "POST",
		mode: Config.somewear.cors ? "cors" : "same-origin",
		headers,
		body: JSON.stringify({ requestedDocsOrigin: context.requestedDocsOrigin }),
	});
	if (!response.ok) throw new DocsSessionRequestError(response.status);
	return (await response.json()) as DocsSession;
}
